/**
 * prefetch.js — invisible route-level data prefetcher + SWR persistence.
 *
 * THREE LAYERS, all transparent to consumer code (no per-page changes):
 *
 *  ┌──────────────────────────────────────────────────────────────────┐
 *  │ Layer 1: in-memory cache (15s TTL, multi-use)                    │
 *  │   – bottom-nav touch → prefetch() → warm in-flight Promise       │
 *  │   – consumer's axios.get sees the Promise → no duplicate fetch   │
 *  ├──────────────────────────────────────────────────────────────────┤
 *  │ Layer 2: localStorage SWR ("stale-while-revalidate", 24h TTL)    │
 *  │   – every successful GET response is persisted                   │
 *  │   – on cold boot, the adapter returns the stale response         │
 *  │     INSTANTLY (resolved Promise) and kicks off the real fetch    │
 *  │     in the background. Localstorage gets refreshed silently.     │
 *  │   – user sees their last-seen tenant list / KPIs / collections   │
 *  │     within ~10ms of opening the app, not 800ms                   │
 *  ├──────────────────────────────────────────────────────────────────┤
 *  │ Layer 3: live network fetch (always runs eventually)             │
 *  └──────────────────────────────────────────────────────────────────┘
 *
 * Safety
 * ──────
 * - GET only. POST/PUT/DELETE pass through untouched (never cached, never
 *   persisted).
 * - URL is the cache key. Query params count.
 * - localStorage payloads >100KB are dropped (saves quota for many small
 *   entries instead of one big one).
 * - 24h SWR TTL: stale-but-shown for up to a day if user is offline.
 * - Background-refresh failure is silent — the consumer already received the
 *   stale response, app keeps working.
 * - On 401/403 the persisted entry is purged (auth changed, don't show
 *   another user's data).
 */
import axios from 'axios';

// ─── In-memory layer ──────────────────────────────────────────────────────
const TTL_MS = 15_000;
const cache = new Map(); // url -> { promise, ts }

export function prefetch(url, token) {
  if (!url) return null;
  const existing = cache.get(url);
  if (existing && Date.now() - existing.ts < TTL_MS) return existing.promise;

  const promise = axios.get(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    __isPrefetch: true,
  }).catch((err) => {
    cache.delete(url);
    throw err;
  });

  cache.set(url, { promise, ts: Date.now() });
  setTimeout(() => {
    const cur = cache.get(url);
    if (cur && cur.promise === promise) cache.delete(url);
  }, TTL_MS);
  return promise;
}

function consume(url) {
  if (!url) return null;
  const entry = cache.get(url);
  if (!entry) return null;
  if (Date.now() - entry.ts > TTL_MS) {
    cache.delete(url);
    return null;
  }
  return entry.promise;
}

// ─── localStorage SWR layer ───────────────────────────────────────────────
const SWR_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const SWR_MAX_BYTES = 100 * 1024;       // 100KB per entry
const SWR_PREFIX = 'swr:';

// Endpoints to skip for SWR persistence. Auth checks are cheap and shouldn't
// be cached (don't want to show stale user identity). Anything that mutates
// or is sensitive should be added here.
const SWR_EXCLUDE = [
  '/api/auth/',
  '/api/sentry-debug',
  '/api/health',
];

function shouldSwr(url) {
  if (!url) return false;
  return !SWR_EXCLUDE.some((p) => url.includes(p));
}

function swrKey(url) {
  // Strip the BACKEND_URL prefix so the key is short and stable across envs.
  // (User shouldn't see different envs in localStorage anyway, but defensive.)
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return SWR_PREFIX + (url.startsWith(base) ? url.slice(base.length) : url);
}

function getStale(url) {
  if (typeof localStorage === 'undefined' || !shouldSwr(url)) return null;
  try {
    const raw = localStorage.getItem(swrKey(url));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (!ts || Date.now() - ts > SWR_TTL_MS) {
      localStorage.removeItem(swrKey(url));
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setStale(url, data) {
  if (typeof localStorage === 'undefined' || !shouldSwr(url)) return;
  try {
    const payload = JSON.stringify({ data, ts: Date.now() });
    if (payload.length > SWR_MAX_BYTES) return; // skip huge responses
    localStorage.setItem(swrKey(url), payload);
  } catch {
    // Quota exceeded — try to free space by purging old SWR entries.
    try {
      const keysToDrop = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k && k.startsWith(SWR_PREFIX)) keysToDrop.push(k);
      }
      keysToDrop.slice(0, 10).forEach((k) => localStorage.removeItem(k));
    } catch { /* localStorage purge failed — quota truly exhausted */ }
  }
}

function clearStale(url) {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(swrKey(url)); } catch { /* localStorage unavailable in this context */ }
}

/**
 * Drop ALL persisted SWR entries. Call on logout so the next user doesn't
 * see the previous user's data on cold boot.
 */
export function clearAllSwr() {
  if (typeof localStorage === 'undefined') return;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(SWR_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch { /* localStorage unavailable in this context */ }
  cache.clear();
}

// ─── Axios adapter wrapper ────────────────────────────────────────────────
let _installed = false;
export function installPrefetchAdapter() {
  if (_installed) return;
  _installed = true;

  let originalAdapter = axios.defaults.adapter;
  if (typeof originalAdapter !== 'function') {
    try {
      originalAdapter = axios.getAdapter ? axios.getAdapter(['xhr', 'fetch']) : null;
    } catch (_) { /* SWR unavailable — graceful fallthrough */ }
  }
  if (typeof originalAdapter !== 'function') {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[prefetch] could not resolve a base axios adapter — disabled');
    }
    _installed = false;
    return;
  }

  /**
   * Returns an axios-shaped response object built from cached data.
   * Used by the SWR path so the consumer page sees the same shape it
   * would have got from a real network call.
   */
  function staleResponse(config, data) {
    return {
      data,
      status: 200,
      statusText: 'OK (swr-stale)',
      headers: { 'x-swr-source': 'localStorage' },
      config,
      request: null,
    };
  }

  axios.defaults.adapter = async function patchedAdapter(config) {
    const isGet = (config.method || 'get').toLowerCase() === 'get';
    const url = config.url;

    if (!isGet || !url) {
      // Mutations bypass everything.
      return originalAdapter(config);
    }

    // Layer 1: in-memory cache hit (warm Promise from bottom-nav touch or
    // another concurrent consumer). Skip when this IS the prefetch call.
    if (!config.__isPrefetch) {
      const cached = consume(url);
      if (cached) {
        try { return await cached; } catch { /* fall through to layer 2/3 */ }
      }
    }

    // Layer 2: SWR — return persisted response INSTANTLY, kick off a real
    // network refresh in the background. Only applied when:
    //   – this is NOT a prefetch call (those are the background refreshers
    //     themselves; recursion would loop forever)
    //   – caller didn't explicitly opt out via config.__noStale
    if (!config.__isPrefetch && !config.__noStale) {
      const stale = getStale(url);
      if (stale != null) {
        // Background refresh — don't block return. Persist new data on success.
        const refresh = originalAdapter({ ...config, __noStale: true })
          .then((resp) => {
            if (resp && resp.status >= 200 && resp.status < 300) {
              setStale(url, resp.data);
            } else if (resp && (resp.status === 401 || resp.status === 403)) {
              clearStale(url);
            }
            return resp;
          })
          .catch((err) => {
            // 401/403 → user/token changed. Purge stale entry so the next
            // cold boot doesn't keep showing the previous user's data.
            const st = err?.response?.status;
            if (st === 401 || st === 403) clearStale(url);
            // Otherwise: ignore, the stale data is already in the consumer's hands.
            return null;
          });

        // Drop the refresh into the in-memory cache so any sibling consumer
        // in the next 15s receives the FRESH data instead of more stale.
        cache.set(url, { promise: refresh, ts: Date.now() });
        setTimeout(() => {
          const cur = cache.get(url);
          if (cur && cur.promise === refresh) cache.delete(url);
        }, TTL_MS);

        return staleResponse(config, stale);
      }
    }

    // Layer 3: real network call. Persist successful response.
    const resp = await originalAdapter(config);
    if (resp && resp.status >= 200 && resp.status < 300) {
      setStale(url, resp.data);
    }
    return resp;
  };
}
